/**
 * Adds documents you already have to one collection.
 *
 * Distinct from `Upload`, which is above it on the same screen: that one brings new bytes
 * in and parses them, this one only changes membership. The two are separate affordances
 * because they answer different questions — "I have a file" and "I have a document".
 *
 * Only documents *not* already in the collection are offered: a checkbox that is already
 * true and cannot be turned off here is a control that does nothing.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "../lib/api";
import { ago, bytes } from "../lib/format";
import type { BulkResult, DocumentList } from "../lib/types";
import { Button, Chip, Empty, Failed, Loading, STATUS_TONE } from "./ui";

/** Matches the bulk endpoint's own ceiling, so a selection can never be refused wholesale. */
const MAX_ADD = 200;

export function AddDocuments({
  collectionId,
  onDone,
}: {
  collectionId: string;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState("");
  const [failures, setFailures] = useState<{ id: string; error: string }[]>([]);

  // The whole first page, filtered client-side: the server has no "not in collection"
  // filter, and `uncollected=true` is not the same question — a document can be in some
  // other collection and still be missing from this one.
  const listing = useQuery({
    queryKey: ["documents", "add-to-collection"],
    queryFn: () => get<DocumentList>(`/v1/documents?limit=${MAX_ADD}`),
  });

  const candidates = useMemo(() => {
    const all = listing.data?.documents ?? [];
    const needle = search.trim().toLowerCase();
    return all
      .filter((doc) => !doc.collections.some((one) => one.id === collectionId))
      .filter((doc) =>
        needle === ""
          ? true
          : (doc.filename ?? "").toLowerCase().includes(needle) ||
            doc.tags.some((tag) => tag.name.toLowerCase().includes(needle)),
      );
  }, [listing.data, collectionId, search]);

  const add = useMutation({
    mutationFn: () =>
      post<BulkResult>("/v1/documents/bulk", {
        ids: [...selected],
        action: "add_collections",
        collections: [collectionId],
      }),
    onSuccess: (result) => {
      setFailures(result.failed);
      if (result.failed.length === 0) {
        void client.invalidateQueries();
        onDone();
      }
    },
  });

  return (
    <section className="shrink-0 border border-hair bg-surface">
      <header className="flex items-center justify-between border-b border-hair px-3 py-2">
        <h3 className="font-mono text-[11px] font-bold tracking-[0.16em] uppercase">
          add documents
        </h3>
        <Button variant="ghost" onClick={onDone}>
          close
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-hair px-3 py-2">
        <input
          aria-label="find a document"
          placeholder="name or tag"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="min-w-48 border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
        />
        <span className="font-mono text-[10.5px] tracking-[0.14em] text-faint uppercase">
          {selected.size} selected
        </span>
        <Button
          variant="primary"
          className="ms-auto"
          disabled={selected.size === 0 || add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? "adding…" : `add ${selected.size || ""}`.trim()}
        </Button>
      </div>

      {listing.isPending ? (
        <Loading what="documents" />
      ) : listing.isError ? (
        <Failed error={listing.error} what="documents" />
      ) : candidates.length === 0 ? (
        <Empty
          title={search ? "nothing matches" : "everything is already here"}
          hint={
            search
              ? "No document outside this collection matches that."
              : "Every document you have is already in this collection. Upload a file above to add a new one."
          }
        />
      ) : (
        <ul className="max-h-72 overflow-auto">
          {candidates.map((doc) => {
            const checked = selected.has(doc.id);
            return (
              <li key={doc.id} className="border-b border-hair-soft last:border-b-0">
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-hair-soft">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setSelected((current) => {
                        const next = new Set(current);
                        if (checked) next.delete(doc.id);
                        else next.add(doc.id);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px]">
                    {doc.filename ?? <span className="text-faint italic">unnamed</span>}
                  </span>
                  <Chip tone={STATUS_TONE[doc.status] ?? "mut"} dot>
                    {doc.status}
                  </Chip>
                  <span className="tnum font-mono text-[11px] text-faint">
                    {bytes(doc.byte_size)}
                  </span>
                  <span className="text-[12px] text-dim">{ago(doc.created_at)}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {failures.length > 0 && (
        <ul className="border-t border-hair px-3 py-2">
          {failures.map((failure) => (
            <li key={failure.id} className="font-mono text-[11px] text-crit">
              {failure.id} — {failure.error}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
